import { Timer } from 'lucide-react';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useGetAiUsageOverview } from '../../../../queries';

/**
 * Ile zostało do miesięcznego resetu zużycia AI.
 *
 * Termin liczy BACKEND (`nextResetAt` z `v3-getAiUsageOverview`), bo to on zna
 * harmonogram crona. Tutaj tylko odliczamy — inaczej „28. o 23:00 w Warszawie"
 * musiałoby być zaszyte drugi raz w przeglądarce i mogłoby rozjechać się
 * z faktycznym cronem.
 */

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #c18344;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 700;
  color: #6b7280;
`;

const IconWrap = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: #7c3aed;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Value = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #111827;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
`;

const Subline = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

/** Odmiana rzeczownika po liczbie: 1 dzień, 2 dni, 5 dni. */
const plural = (count: number, one: string, few: string, many: string): string => {
  if (count === 1) return one;
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 12 && lastTwo <= 14) return many;
  return last >= 2 && last <= 4 ? few : many;
};

/**
 * Odliczanie w największej sensownej jednostce. Przy dwóch tygodniach nikogo
 * nie interesują minuty, a w ostatniej godzinie liczy się już tylko ona.
 */
const formatRemaining = (ms: number): string => {
  if (ms <= 0) return 'trwa teraz';

  const minutes = Math.floor(ms / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days >= 1) {
    const restHours = hours - days * 24;
    const dayPart = `${days} ${plural(days, 'dzień', 'dni', 'dni')}`;
    if (days >= 7 || restHours === 0) return dayPart;
    return `${dayPart} ${restHours} ${plural(restHours, 'godzina', 'godziny', 'godzin')}`;
  }
  if (hours >= 1) {
    return `${hours} ${plural(hours, 'godzina', 'godziny', 'godzin')}`;
  }
  return `${minutes} ${plural(minutes, 'minuta', 'minuty', 'minut')}`;
};

export const AiResetCountdown: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useGetAiUsageOverview();

  // Tyka co minutę: przy odliczaniu liczonym w godzinach i dniach częściej
  // nie ma czego odświeżać.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const value = useMemo(() => {
    if (isLoading) return '…';
    if (isError || !data?.nextResetAt) return 'brak danych';
    return formatRemaining(data.nextResetAt - now);
  }, [data, isLoading, isError, now]);

  const subline = useMemo(() => {
    if (isLoading || isError || !data?.nextResetAt) return 'Do miesięcznego resetu zużycia AI';
    const when = new Date(data.nextResetAt).toLocaleString('pl-PL', {
      day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit',
    });
    const orgs = data.autoResetOrganizations;
    return `${when}, obejmie ${orgs} ${plural(orgs, 'organizację', 'organizacje', 'organizacji')}`;
  }, [data, isLoading, isError]);

  return (
    <Card onClick={() => navigate('/ai-usage')}>
      <Header>
        <IconWrap><Timer size={18} /></IconWrap>
        Reset zużycia AI
      </Header>
      <Value>{value}</Value>
      <Subline>{subline}</Subline>
    </Card>
  );
};
